/**
 * Access rules for the /uniform-preview route.
 *
 * The route renders unpublished draft compositions, so it historically existed
 * only in non-production environments and "which environment am I?" was the
 * whole access decision. It is now also served in production so the WAC
 * migration tooling can render and visually QA the compositions it builds,
 * which means that single check is no longer sufficient. The real rules live
 * here so the router and the SSR page-props resolver apply the same ones.
 *
 * There is deliberately no shared-secret check. wac-web has no runtime secret
 * channel — every env var reaching this code is baked in at build time from
 * .env.* and, if listed in `inlineEnvironment`, inlined into the browser
 * bundle — so a "secret" here would either be undefined at runtime or public.
 * What guards the route instead: it is absent from customer-facing hostnames,
 * marked noindex/nofollow in the SSR HTML, restricted to an explicit project
 * allow-list, and useless without a valid compositionId + projectId pair. The
 * composition itself is still fetched via wac-management-service, which holds
 * the Uniform API key server-side.
 *
 * Deliberately free of imports and side effects: the router runs during client
 * hydration as well as during SSR, so anything reachable from it ends up in the
 * browser bundle.
 */

/**
 * wac-web's own Bifrost hostnames, as opposed to a customer-facing domain such
 * as www.atlassian.com. Mirrors the Host regex used by the ingress routes in
 * wac-web.sd.yml.
 */
const INTERNAL_WAC_WEB_HOSTNAME =
	/^wac-web\.(ddev|stg-east|prod-east)\.frontend\.public\.atl-paas\.net$/;

export const isInternalWacWebHostname = (hostname: string): boolean =>
	INTERNAL_WAC_WEB_HOSTNAME.test(hostname);

/** Parses the comma-separated UNIFORM_PREVIEW_ALLOWED_PROJECT_IDS value. */
export const parseAllowedProjectIds = (raw: string | undefined): string[] =>
	(raw ?? '')
		.split(',')
		.map((projectId) => projectId.trim())
		.filter(Boolean);

/**
 * Whether a projectId may be rendered outside a preview environment.
 *
 * An allow-list rather than the deny-list used in preview environments, which
 * compares against the environment's own UNIFORM_PROJECT_ID. Production does
 * not set UNIFORM_PROJECT_ID at all, so that comparison would treat every
 * requested projectId as a permitted dev project — a deny-list that defaults to
 * allow. This defaults to deny instead.
 */
export const isProjectAllowed = (
	requestedProjectId: string | null,
	allowedProjectIds: readonly string[],
): boolean =>
	Boolean(requestedProjectId) && allowedProjectIds.includes(requestedProjectId as string);
